import { useMemo } from "react";
import { calcRequiredFTE, calcEffectiveHours, calcWasteFootprint } from "../../utils/calculations";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { TrendingUp, TrendingDown, Users, AlertTriangle } from "lucide-react";

const CHART_COLORS = { "Front Office": "#7C9A7E", "Middle Office": "#4B5E4D", "Back Office": "#A3B8A5", "Support": "#C5D6C7" };

function KpiTile({ label, value, sub, color, icon: Icon, trend }) {
  return (
    <div className="bg-white border border-[#E2E8E3] rounded-md p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wider text-sage-500 font-semibold mb-1">{label}</p>
          <p className={`font-heading text-2xl font-semibold ${color || "text-sage-900"}`}>{value}</p>
          {sub && <p className="text-xs text-sage-400 mt-0.5">{sub}</p>}
        </div>
        {Icon && <div className="w-8 h-8 bg-sage-50 rounded-md flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-sage-500" /></div>}
      </div>
    </div>
  );
}

export default function CurrentStateTab({ project, effectiveHours }) {
  const setup = project?.setup || {};
  const stages = project?.stages || [];
  const vc = setup.volume_capacity || {};
  const qw = setup.quality_waste || {};
  const org = setup.org_profile || {};
  const totalVolume = vc.total_monthly_volume || 0;
  const costPerFTE = vc.cost_per_fte || 15000;
  const eff = effectiveHours || calcEffectiveHours(setup.effective_hours);

  const stagesWithCalc = useMemo(() => {
    return stages.map((s) => ({ ...s, reqFTE: calcRequiredFTE(s, totalVolume, eff) }));
  }, [stages, totalVolume, eff]);

  const totalLocals = stages.reduce((a, s) => a + (s.local_count || 0), 0);
  const totalExpats = stages.reduce((a, s) => a + (s.expat_count || 0), 0);
  const totalTeam = totalLocals + totalExpats;
  const totalReqFTE = stagesWithCalc.reduce((a, s) => a + s.reqFTE, 0);
  const delta = totalTeam - totalReqFTE;
  const avgUtil = stages.length > 0 ? (stages.reduce((a, s) => a + (s.utilization || 75), 0) / stages.length).toFixed(0) : 0;
  const avgAHT = stages.length > 0 ? (stages.reduce((a, s) => a + (s.aht || 10), 0) / stages.length).toFixed(0) : 0;
  const monthlyCost = totalTeam * costPerFTE;
  const localPct = totalTeam > 0 ? (totalLocals / totalTeam * 100).toFixed(1) : 0;

  const waste = calcWasteFootprint(qw, totalTeam);

  const chartData = stagesWithCalc.map((s) => ({
    name: s.stage_name?.length > 12 ? s.stage_name.slice(0, 12) + "…" : s.stage_name,
    reqFTE: parseFloat(s.reqFTE.toFixed(1)),
    current: (s.local_count || 0) + (s.expat_count || 0),
    type: s.stage_type,
  }));

  return (
    <div className="animate-fade-in">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Step 4</p>
        <h2 className="font-heading text-2xl font-semibold text-sage-900">Current State Snapshot</h2>
        <p className="text-sage-500 text-sm mt-1">Summary KPIs and visual analytics for the current staffing model</p>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiTile label="Total FTE" value={totalTeam} sub={`${localPct}% local nationals`} icon={Users} />
        <KpiTile label="Required FTE" value={totalReqFTE.toFixed(1)} sub="Modelled capacity demand" />
        <KpiTile label="Headcount Delta" value={`${delta >= 0 ? "+" : ""}${delta.toFixed(1)}`} color={delta > 1 ? "text-amber-600" : delta < -1 ? "text-red-600" : "text-green-600"} sub={delta > 1 ? "Over-staffed" : delta < -1 ? "Under-staffed" : "Right-sized"} />
        <KpiTile label="Monthly Cost" value={`${org.currency || "USD"} ${(monthlyCost / 1000).toFixed(0)}K`} sub="At current headcount" />
        <KpiTile label="Local Nationals" value={totalLocals} sub={`${eff.monthlyHrsLocal?.toFixed(0)} hrs/month each`} />
        <KpiTile label="Expatriates" value={totalExpats} sub={`${eff.monthlyHrsExpat?.toFixed(0)} hrs/month each`} />
        <KpiTile label="Avg Utilisation" value={`${avgUtil}%`} sub={avgUtil > 90 ? "High — risk of burnout" : avgUtil < 65 ? "Low — underutilised" : "Within target range"} color={avgUtil > 90 ? "text-amber-600" : avgUtil < 65 ? "text-blue-600" : "text-sage-900"} />
        <KpiTile label="Avg AHT" value={`${avgAHT} min`} sub="Weighted average handle time" />
      </div>

      {/* FTE Demand Chart */}
      {chartData.length > 0 && (
        <div className="bg-white border border-[#E2E8E3] rounded-md p-5 mb-4">
          <h3 className="font-heading text-base font-semibold text-sage-800 mb-4">FTE Demand by Stage</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 20, bottom: 0, left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAF0EA" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#8FA891" }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#4B5E4D" }} width={80} />
              <Tooltip formatter={(val, name) => [val, name === "reqFTE" ? "Required FTE" : "Current Team"]} contentStyle={{ borderColor: "#E2E8E3", borderRadius: 6, fontSize: 12 }} />
              <Bar dataKey="reqFTE" name="Required FTE" radius={[0, 3, 3, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={CHART_COLORS[entry.type] || "#7C9A7E"} />
                ))}
              </Bar>
              <Bar dataKey="current" name="Current Team" fill="#E2E8E3" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            {Object.entries(CHART_COLORS).map(([type, color]) => (
              <div key={type} className="flex items-center gap-1.5 text-xs text-sage-500">
                <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: color }} />
                {type}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Capacity Utilisation */}
      {stages.length > 0 && (
        <div className="bg-white border border-[#E2E8E3] rounded-md p-5 mb-4">
          <h3 className="font-heading text-base font-semibold text-sage-800 mb-4">Capacity Utilisation</h3>
          <div className="space-y-3">
            {stages.map((s, i) => {
              const util = s.utilization || 75;
              const barColor = util > 90 ? "#D97706" : util < 65 ? "#A3B8A5" : "#7C9A7E";
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-32 text-xs text-sage-700 truncate flex-shrink-0">{s.stage_name}</div>
                  <div className="flex-1 bg-sage-50 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all duration-300" style={{ width: `${util}%`, backgroundColor: barColor }} />
                  </div>
                  <div className="w-10 text-right text-xs font-semibold text-sage-700">{util}%</div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-sage-400">
            <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-sage-500 inline-block" /> 65–90% (Target)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-amber-400 inline-block" /> {">"}90% (High)</span>
            <span className="flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-sage-300 inline-block" /> {"<"}65% (Low)</span>
          </div>
        </div>
      )}

      {/* Waste Footprint */}
      <div className="bg-white border border-[#E2E8E3] rounded-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          <h3 className="font-heading text-base font-semibold text-sage-800">Waste Footprint Analysis</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            { label: "NVA Time", value: `${waste.nva}%`, sub: `~${waste.nvaFteHours} FTE-equivalent hrs/mo`, color: waste.nva > 20 ? "text-red-600" : waste.nva > 15 ? "text-amber-600" : "text-sage-700" },
            { label: "Rework Load", value: `${waste.rework}%`, sub: `~${waste.reworkFteHours} FTE-equivalent hrs/mo`, color: waste.rework > 10 ? "text-red-600" : waste.rework > 5 ? "text-amber-600" : "text-sage-700" },
            { label: "Absenteeism", value: `${waste.absenteeism}%`, sub: `~${waste.absentFte} FTE-equivalents`, color: waste.absenteeism > 8 ? "text-red-600" : waste.absenteeism > 5 ? "text-amber-600" : "text-sage-700" },
            { label: "Total Waste %", value: `${waste.totalWastePct}%`, sub: "Lean benchmark: <10%", color: Number(waste.totalWastePct) > 20 ? "text-red-600" : Number(waste.totalWastePct) > 10 ? "text-amber-600" : "text-green-600" },
          ].map(({ label, value, sub, color }) => (
            <div key={label} className="bg-sage-50 rounded-md p-3 text-center">
              <p className="text-xs text-sage-500 uppercase tracking-wider font-semibold mb-1">{label}</p>
              <p className={`font-heading text-xl font-bold ${color}`}>{value}</p>
              <p className="text-xs text-sage-400 mt-0.5">{sub}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
